import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { FiDownload, FiArrowLeft, FiCreditCard } from 'react-icons/fi';
import { ordersAPI, paymentsAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import OrderTimeline from '../components/OrderTimeline';
import Loading from '../components/Loading';
import { toast } from 'react-toastify';

const OrderDetailPage = () => {
  const { id } = useParams();
  const { user } = useAuth();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [paying, setPaying] = useState(false);

  const refetchOrder = async () => {
    try {
      const { data } = await ordersAPI.getOne(id);
      if (data.success) setOrder(data.order);
    } catch {
      // Payment webhooks may still update the order even if this refresh fails.
    }
  };

  const handleDownloadInvoice = async () => {
    try {
      setDownloading(true);
      const response = await ordersAPI.downloadInvoice(order._id);
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Invoice-${order.orderNumber}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Invoice download failed:', error);
      alert('Failed to download invoice. Please try again.');
    } finally {
      setDownloading(false);
    }
  };

  const handlePayNow = async () => {
    if (!window.Razorpay) {
      toast.error('Payment gateway unavailable. Please try again later.');
      return;
    }

    setPaying(true);
    try {
      const paymentRes = await paymentsAPI.createOrder({ orderId: order._id });
      const { razorpayOrder, key } = paymentRes.data;

      const options = {
        key,
        amount: razorpayOrder.amount,
        currency: razorpayOrder.currency,
        name: 'Swaad Pickles',
        description: `Order ${order.orderNumber}`,
        order_id: razorpayOrder.id,
        handler: async (response) => {
          try {
            const { data } = await paymentsAPI.verify(response);
            if (data.success && data.order) {
              setOrder(data.order);
            } else {
              await refetchOrder();
            }
            toast.success('Payment successful! 🎉');
          } catch {
            await refetchOrder();
            toast.warning('Payment verification pending. We\'ll update you soon.');
          } finally {
            setPaying(false);
          }
        },
        prefill: {
          name: user?.name,
          email: user?.email,
          contact: order.phone || user?.phone,
        },
        theme: {
          color: '#D4421E',
        },
        modal: {
          ondismiss: async () => {
            setPaying(false);
            await refetchOrder();
            toast.info('Payment dismissed. You can retry payment before dispatch.');
          },
        },
      };

      const rzp = new window.Razorpay(options);
      rzp.open();
    } catch (error) {
      setPaying(false);
      toast.error(error.response?.data?.message || 'Unable to start payment. Please try again.');
    }
  };

  useEffect(() => {
    const fetchOrder = async () => {
      try {
        const { data } = await ordersAPI.getOne(id);
        if (data.success) {
          setOrder(data.order);
        }
      } catch (error) {
        console.error('Error fetching order:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchOrder();
  }, [id]);

  if (loading) return <Loading message="Loading order details..." />;

  if (!order) {
    return (
      <div className="page container">
        <div className="empty-state">
          <div className="emoji">❌</div>
          <h3>Order not found</h3>
          <Link to="/orders" className="btn btn-primary" style={{ width: 'auto', marginTop: 20 }}>
            <FiArrowLeft /> Back to Orders
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="page container" style={{ maxWidth: 800, margin: '0 auto' }}>
      <Link to="/orders" style={{ color: 'var(--primary)', display: 'inline-flex', alignItems: 'center', gap: 6, marginBottom: 24, fontWeight: 600, fontSize: '0.9rem', transition: 'opacity 0.2s' }}>
        <FiArrowLeft /> Back to Orders
      </Link>

      <div className="order-card" style={{ marginBottom: 20 }}>
        <div className="order-card-header">
          <div>
            <div className="order-number" style={{ fontSize: '1.3rem' }}>{order.orderNumber}</div>
            <div className="order-date">
              Placed on {new Date(order.createdAt).toLocaleDateString('en-IN', {
                day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit',
              })}
            </div>
          </div>
          <button
            onClick={handleDownloadInvoice}
            className="btn btn-sm"
            style={{ background: 'var(--text-heading)', color: 'white', cursor: (downloading || order.status === 'Cancelled') ? 'not-allowed' : 'pointer', opacity: order.status === 'Cancelled' ? 0.4 : 1 }}
            disabled={downloading || order.status === 'Cancelled'}
            title={order.status === 'Cancelled' ? 'Invoice not available for cancelled orders' : ''}
          >
            <FiDownload /> {downloading ? 'Downloading...' : 'Download Invoice'}
          </button>
        </div>

        {/* Items */}
        <h4 style={{ marginBottom: 14, color: 'var(--text-heading)', display: 'flex', alignItems: 'center', gap: 7 }}>🫙 Order Items</h4>
        {order.items.map((item, idx) => (
          <div key={idx} className="cart-item" style={{ marginBottom: 10 }}>
            <div style={{
              fontSize: '2rem', width: 44, height: 44, display: 'flex', alignItems: 'center',
              justifyContent: 'center', background: 'linear-gradient(135deg,#FFF3E0,#FFDAB9)',
              borderRadius: 'var(--radius-sm)', flexShrink: 0,
            }}>🥒</div>
            <div className="cart-item-info">
              <div className="cart-item-name">{item.productName}</div>
              <div className="cart-item-details">
                {item.quantityKg} kg × ₹{item.pricePerKg}/kg
              </div>
            </div>
            <div className="cart-item-total">₹{item.itemTotal}</div>
          </div>
        ))}

        <div className="cart-total-row grand-total" style={{ marginTop: 15 }}>
          <span>Total Amount</span>
          <span>₹{order.totalAmount}</span>
        </div>

        <div style={{ marginTop: 12, fontSize: '0.875rem', color: 'var(--text-medium)', display: 'flex', alignItems: 'center', gap: 10 }}>
          <span><strong>Payment:</strong> </span>
          <span className={`payment-chip ${
            order.paymentStatus === 'Paid' ? 'paid' :
            order.paymentStatus === 'Failed' ? 'failed' : 'pending'
          }`}>{order.paymentStatus}</span>
        </div>

        {/* Payment Status Banner */}
        {['Pending', 'Failed'].includes(order.paymentStatus) && order.status !== 'Cancelled' && (
          <div style={{ marginTop: 16, padding: '14px 18px', background: '#FEF3C7', borderRadius: 'var(--radius-md)', border: '1px solid #FCD34D', display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div>
              <div style={{ fontWeight: 700, color: '#92400E', marginBottom: 4, display: 'flex', alignItems: 'center', gap: 6 }}>
                ⚠️ {order.paymentStatus === 'Failed' ? 'Payment Failed' : 'Payment Pending'}
              </div>
              <div style={{ fontSize: '0.82rem', color: '#78350F' }}>Your order is placed. You can pay online now or complete payment before dispatch.</div>
            </div>
            {['Ready for Dispatch', 'Packed', 'Under Manufacturing', 'Order Received', 'Order Placed'].some(s => s === order.status) && (
              <button
                className="btn btn-sm"
                style={{ background: 'var(--primary)', color: 'white', alignSelf: 'flex-start' }}
                onClick={handlePayNow}
                disabled={paying}
              >
                <FiCreditCard /> {paying ? 'Opening Payment...' : order.paymentStatus === 'Failed' ? 'Retry Payment' : 'Pay Now'}
              </button>
            )}
          </div>
        )}
        {order.paymentStatus === 'Paid' && (
          <div style={{ marginTop: 12, padding: '8px 14px', background: '#D1FAE5', borderRadius: 'var(--radius-sm)', border: '1px solid #6EE7B7', fontSize: '0.84rem', color: '#065F46', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6 }}>
            ✅ Payment Completed {order.paymentDate ? `on ${new Date(order.paymentDate).toLocaleDateString('en-IN')}` : ''}
          </div>
        )}

        {/* Tracking ID */}
        {['Dispatched', 'Delivered'].includes(order.status) && order.trackingId && (
          <div style={{ marginTop: 16, padding: '14px 18px', background: '#EFF6FF', borderRadius: 'var(--radius-md)', border: '1px solid #BFDBFE' }}>
            <h4 style={{ marginBottom: 10, color: '#1D4ED8', display: 'flex', alignItems: 'center', gap: 6 }}>🚚 Shipment Tracking</h4>
            {order.courierName && (
              <div style={{ fontSize: '0.84rem', color: 'var(--text-medium)', marginBottom: 6 }}>
                <strong>Courier:</strong> {order.courierName}
              </div>
            )}
            <div style={{ fontSize: '1rem', color: '#1E40AF', fontWeight: 800, fontFamily: 'Inter, monospace', letterSpacing: 1 }}>
              {order.trackingId}
            </div>
            <div style={{ fontSize: '0.78rem', color: 'var(--text-light)', marginTop: 6 }}>Use this ID to track your order on the courier website.</div>
          </div>
        )}

        {/* Shipping Address */}
        {order.shippingAddress?.street && (
          <div style={{ marginTop: 16, padding: '14px 18px', background: 'var(--bg-muted)', borderRadius: 'var(--radius-md)', border: '1px solid var(--border-light)' }}>
            <h4 style={{ marginBottom: 10, color: 'var(--text-heading)', display: 'flex', alignItems: 'center', gap: 6 }}>📍 Delivery Address</h4>
            <p style={{ fontSize: '0.875rem', color: 'var(--text-medium)', lineHeight: 1.65 }}>
              {order.shippingAddress.street}<br />
              {order.shippingAddress.city}, {order.shippingAddress.state} — {order.shippingAddress.pincode}
            </p>
            {order.phone && <p style={{ fontSize: '0.875rem', color: 'var(--text-medium)', marginTop: 6 }}>📞 {order.phone}</p>}
          </div>
        )}
      </div>

      {/* Order Timeline */}
      <div className="order-card">
        <h3 style={{ marginBottom: 24, color: 'var(--text-heading)', display: 'flex', alignItems: 'center', gap: 8 }}>📍 Order Tracking</h3>
        <OrderTimeline
          currentStatus={order.status}
          statusHistory={order.statusHistory || []}
        />
      </div>
    </div>
  );
};

export default OrderDetailPage;
